/**
 * MediaRecorder lifecycle management.
 * Wraps getUserMedia and MediaRecorder, routing audio chunks to a Session.
 * Owns a Timer that tracks elapsed recording time.
 * Single source of truth for all state — from idle through upload complete.
 */
import IndexedDBSessionStore from "./session_stores/indexed_db.js";
import MemorySessionStore from "./session_stores/memory.js";
import Timer from "./timer.js";

const SESSION_STORES = {
  memory: MemorySessionStore,
  indexed_db: IndexedDBSessionStore
};

const TIMESLICE_MS = 10000;
const BITRATE = 32000;

// Ordered by preference. "upload" uses the base MIME type without codec params
// because Rails' DiskController strips params from request.content_type.
const SUPPORTED_FORMATS = [
  { recording: "audio/webm;codecs=opus", upload: "audio/webm", extension: "webm" },
  { recording: "audio/mp4",              upload: "audio/mp4",  extension: "mp4"  }
];

export default class Engine {
  constructor({ onStateChange, store }) {
    this.sessionStore = this.buildSessionStore(store);
    this.format = this.detectFormat();
    this.onStateChange = onStateChange;
    this.session = null;
    this.chunkIndex = 0;
    this.mediaRecorder = null;
    this.stream = null;
    this.file = null;
    this.timer = new Timer({ onTick: () => this.emitState() });

    this.state = {
      status: "idle",
      progress: null,
      message: null
    };
  }

  async start() {
    this.setState({ status: "requesting", progress: null, message: null });

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      this.setState({ status: "error", message: "Microphone access denied" });
      return false;
    }

    if (!this.format) {
      this.releaseStream();
      this.setState({ status: "error", message: "Your browser does not support audio recording" });
      return false;
    }

    this.session = await this.sessionStore.create();
    this.chunkIndex = 0;

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: this.format.recording,
      audioBitsPerSecond: BITRATE
    });

    this.mediaRecorder.ondataavailable = async (event) => {
      if (event.data.size === 0) return;

      await this.session.addChunk(this.chunkIndex, event.data);
      this.chunkIndex++;
      this.emitState();
    };

    this.mediaRecorder.start(TIMESLICE_MS);
    this.state.status = "recording";
    this.timer.start();
    return true;
  }

  async pause() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.pause();
      this.timer.pause();
      await this.session.update({ status: "paused" });
      this.setState({ status: "paused" });
    }
  }

  async resume() {
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      this.mediaRecorder.resume();
      this.timer.resume();
      await this.session.update({ status: "recording" });
      this.setState({ status: "recording" });
    }
  }

  stop() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = async () => {
        await this.session.update({ status: "completed" });
        this.releaseStream();
        this.timer.stop();

        const chunks = await this.session.chunks();
        const blob = new Blob(chunks, { type: this.format.upload });
        this.file = new File([blob], this.generateFilename(), { type: this.format.upload });

        this.setState({ status: "uploading", progress: 0 });
        resolve(this.file);
      };

      this.mediaRecorder.stop();
    });
  }

  uploadProgress(progress) {
    this.setState({ status: "uploading", progress: Math.round(progress) });
  }

  async uploadComplete() {
    await this.session.delete();
    this.file = null;
    this.session = null;
    this.chunkIndex = 0;
    this.setState({ status: "complete", progress: null, message: null });
  }

  uploadFailed(message) {
    this.setState({ status: "error", progress: null, message });
  }

  retryUpload() {
    this.setState({ status: "uploading", progress: 0, message: null });
  }

  async checkForRecovery() {
    const sessions = await this.sessionStore.all();

    // Skip sessions belonging to this engine's active recording, and sessions with no chunks
    for (const session of sessions) {
      if (this.session && session.id === this.session.id) continue;
      const chunks = await session.chunks();
      if (chunks.length === 0) continue;

      return session;
    }

    return null;
  }

  async recover(session) {
    this.session = session;
    const chunks = await session.chunks();

    if (chunks.length === 0) {
      await session.delete();
      this.session = null;
      this.file = null;
      return null;
    }

    const blob = new Blob(chunks, { type: this.format.upload });
    this.file = new File([blob], this.generateFilename(), { type: this.format.upload });
    this.setState({ status: "uploading", progress: 0 });
    return this.file;
  }

  async discardRecovery(session) {
    await session.delete();
  }

  destroy() {
    if (this.mediaRecorder) {
      this.mediaRecorder.onstop = null;
      if (this.mediaRecorder.state !== "inactive") this.mediaRecorder.stop();
    }
    this.releaseStream();
    this.timer.stop();
  }

  // private

  setState(changes) {
    Object.assign(this.state, changes);
    this.emitState();
  }

  emitState() {
    this.onStateChange({
      ...this.state,
      elapsed: this.timer.formatted(),
      chunkCount: this.chunkIndex
    });
  }

  generateFilename() {
    const date = new Date().toISOString().split("T")[0];
    const ext = this.format ? this.format.extension : "webm";
    return `meeting-recording-${date}.${ext}`;
  }

  detectFormat() {
    if (typeof MediaRecorder === "undefined") return null;
    return SUPPORTED_FORMATS.find(function (f) { return MediaRecorder.isTypeSupported(f.recording); }) || null;
  }

  buildSessionStore(store) {
    const StoreClass = SESSION_STORES[store] || IndexedDBSessionStore;
    return new StoreClass();
  }

  releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }
}
